/**
 * A document's STORED length (`DeckSource.textLength`) — when it may be
 * trusted, and which documents have one at all.
 *
 * The length is what lets a surface say "X% covered" under a document whose
 * text it has not downloaded: measuring a document means reading its whole
 * extraction, which is why the materials grid once dropped the line. A device
 * that has the text in hand anyway (the document was opened, or generated
 * from) records the length with the document, and every other surface reads
 * it from there.
 *
 * TRUSTED ONLY FOR WHAT IT MEASURED. The length was taken over one file and
 * one page selection; it carries a key for both (`documentLengthKey`). When
 * the entry's file or pages change, the key no longer matches and the length
 * reads as UNKNOWN — no line — until a device with the text measures again.
 * Never a number about pages the deck no longer reads.
 *
 * MEASURABLE = a document with a file — a recording's or a video's included.
 * Their file is the NOTES document made from the transcript: the text
 * Generate reads for them, and the one their cards' `m:` refs are offsets
 * into, so it measures exactly like any other document's. (What a media
 * adds — its note — is `generation/document-length.ts`'s.) Pasted text and
 * placeholders have no file to measure: they never carry a length and never
 * show a figure.
 *
 * The measuring itself is `generation/document-length.ts`, on the same
 * denominator the progress figures use.
 */

import type {
  DeckSource,
  DeckSourceTextLength,
} from '../../sync-engine/schema/shared-types';

type LengthEntry = Pick<DeckSource, 'pdfInfo' | 'textLength'>;

/**
 * The key of the file and page selection a length is measured over. An
 * absent or EMPTY page list is the whole document (`coverage.ts`), so both
 * spell the same key; a selection is order- and duplicate-blind. `undefined`
 * for an entry with no file — nothing to measure.
 */
export function documentLengthKey(
  pdfInfo: { filePath?: string; pages?: readonly number[] | null } | undefined,
): string | undefined {
  const filePath = pdfInfo?.filePath;
  if (!filePath) return undefined;
  const pages = [...new Set(pdfInfo.pages ?? [])].sort((a, b) => a - b);
  return `${filePath}#${pages.join(',')}`;
}

/** The document can carry a length — see the module header. */
export function isMeasurableDocument(entry: Pick<DeckSource, 'pdfInfo'>): boolean {
  return !!entry.pdfInfo?.filePath;
}

function isWellFormed(length: unknown): length is DeckSourceTextLength {
  if (!length || typeof length !== 'object') return false;
  const { totalChars, paragraphCount, pagesKey } = length as Record<string, unknown>;
  return (
    typeof totalChars === 'number' &&
    Number.isFinite(totalChars) &&
    totalChars >= 0 &&
    typeof paragraphCount === 'number' &&
    Number.isFinite(paragraphCount) &&
    paragraphCount > 0 &&
    typeof pagesKey === 'string'
  );
}

/**
 * The entry's stored length IF it still describes the entry — measurable,
 * well formed, and taken over the entry's current file and pages. Anything
 * else is unknown (`undefined`).
 */
export function currentDocumentLength(
  entry: LengthEntry,
): DeckSourceTextLength | undefined {
  if (!isMeasurableDocument(entry)) return undefined;
  const length = entry.textLength;
  if (!isWellFormed(length)) return undefined;
  return length.pagesKey === documentLengthKey(entry.pdfInfo) ? length : undefined;
}

/** {@link currentDocumentLength}'s `totalChars` — the progress figures' input. */
export function storedDocumentLength(entry: LengthEntry): number | undefined {
  return currentDocumentLength(entry)?.totalChars;
}

/**
 * The length a merge keeps for an entry both copies list. The winning copy's
 * CONTENT (name, file, pages) stands; the length is derived from it, so it
 * comes from whichever copy holds one that matches that content — the
 * winner's first. A copy that never measured (a rename made on a device that
 * had not seen the measurement) cannot drop a length the other copy took.
 */
export function mergedDocumentLength(
  winner: LengthEntry,
  loser: LengthEntry,
): DeckSourceTextLength | undefined {
  return (
    currentDocumentLength(winner) ??
    currentDocumentLength({ pdfInfo: winner.pdfInfo, textLength: loser.textLength }) ??
    winner.textLength
  );
}
